import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import LabelInput from '../components/LabelInput'
import LabelOnly from '../components/LabelOnly'
import InputOnly from '../components/InputOnly'
import { gql, useMutation, useQuery } from '@apollo/client'
import ConfirmPopup from '../components/ConfirmPopup'
import AutoCompleteOnly from '../components/AutoCompleteOnly'
import { findAllKepcoTOUGQL } from '../gql/PurchasePlans.gql'
import { findAllRECGQL } from './DataREC'
import { findAllSmpGQL } from './DataSMP'
import { findAllHolidaysGQL } from './DataHoliday'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px #00000029;
  background-color: #fff;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  padding: 16px 24px 16px 24px;
  background: #373a40;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  justify-content: space-between;
  flex-shrink: 0;
`

const Title = styled.div`
  display: flex;
  font-family: SUIT;
  font-size: 22px;
  font-weight: 700;
  line-height: 27px;
  letter-spacing: 0em;
  text-align: left;
  color: #ffffff;
  align-items: center;
`

const SaveButton = styled.div`
  display: flex;

  width: 41px;
  height: 29px;
  padding: 6px 8px 6px 8px;
  border-radius: 6px;
  gap: 4px;
  background-color: #74e3c2;
  cursor: pointer;

  font-size: 14px;
  font-weight: 500;

  align-items: center;
  justify-content: center;
`

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  gap: 40px;
  overflow-y: auto;
`

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 50%;
  input {
    max-width: 136px;
  }
`

const InUnit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PPACost = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InUnitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const InUnitItem = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`

const InUnitLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  width: 200px;
  color: #2f3c5e;
`

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 50%;
  input {
    max-width: 136px;
  }
`

export const findAllUnitDefaultGQL = gql`
  query findAllUnitDefault {
    findAllUnitDefault {
      _id
      powerMarketTransactionUnit
      environmentalUnit
      greenPremiumUnit
      touVer
      recYear
      smpYear
      holidayYear
      internalNetworkUnitBaseUnit
      internalNetworkUnitPowerUnit
      distributionUnitBaseUnit
      distributionUnitUsageUnit
      lossCostMinUnit
      lossCostMidUnit
      lossCostMaxUnit
      additionalSettlementUnit
      transactionCommissionUnit
      discountedPriceUnit
      transmissionLossCostUnit
      ectCostUnit
    }
  }
`

const UpdateDataUnitGQL = gql`
  mutation updateUnitDefault($val: UpdateUnitDefaultInput!) {
    updateUnitDefault(updateUnitDefaultInput: $val) {
      _id
    }
  }
`

interface IDataUnitData {
  _id?: string
  powerMarketTransactionUnit: number
  environmentalUnit: number
  greenPremiumUnit: number
  touVer: string
  recYear: number | string
  smpYear: number | string
  holidayYear: number | string
  internalNetworkUnitBaseUnit: number
  internalNetworkUnitPowerUnit: number
  distributionUnitBaseUnit: number
  distributionUnitUsageUnit: number
  lossCostMinUnit: number
  lossCostMidUnit: number
  lossCostMaxUnit: number
  additionalSettlementUnit: number
  transactionCommissionUnit: number
  discountedPriceUnit: number
  transmissionLossCostUnit: number
  ectCostUnit: number
}

export const initializeDataUnitData = (): IDataUnitData => {
  return {
    powerMarketTransactionUnit: 0,
    environmentalUnit: 0,
    greenPremiumUnit: 11,
    touVer: '',
    recYear: '',
    smpYear: '',
    holidayYear: '',
    internalNetworkUnitBaseUnit: 0,
    internalNetworkUnitPowerUnit: 0,
    distributionUnitBaseUnit: 0,
    distributionUnitUsageUnit: 0,
    lossCostMinUnit: 0,
    lossCostMidUnit: 0,
    lossCostMaxUnit: 0,
    additionalSettlementUnit: 0,
    transactionCommissionUnit: 0,
    discountedPriceUnit: 0,
    transmissionLossCostUnit: 0,
    ectCostUnit: 0,
  }
}

export const DataUnit = () => {
  const { data } = useQuery(findAllUnitDefaultGQL)
  const { data: kepcoData } = useQuery(findAllKepcoTOUGQL)
  const { data: recData } = useQuery(findAllRECGQL)
  const { data: smpData } = useQuery(findAllSmpGQL)
  const { data: holidaysData } = useQuery(findAllHolidaysGQL)
  const [updateDataUnit] = useMutation(UpdateDataUnitGQL)
  const [dataUnit, setDataUnit] = useState<IDataUnitData>(
    initializeDataUnitData
  )
  const [showSavePopup, setShowSavePopup] = useState(false)

  const kepcoOptions =
    kepcoData?.findAllKepcoTOU?.map((item: any) => item.name) || []
  const recOptions = recData?.findAllREC?.map((item: any) => item.year) || []
  const smpOptions = smpData?.findAllSmp?.map((item: any) => item.year) || []
  const holidayOptions =
    holidaysData?.findAllHolidays?.map((item: any) => item.year) || []

  useEffect(() => {
    if (data?.findAllUnitDefault[0]) {
      const { __typename, ...less } = data.findAllUnitDefault[0]
      setDataUnit({
        ...initializeDataUnitData(),
        ...less,
        greenPremiumUnit: less.greenPremiumUnit ?? 11,
      })
    }
  }, [data])

  const handleDataUnitChange = (key: string, newValue: number) => {
    setDataUnit((prevDataUnit) => {
      const updatedDataUnit = { ...prevDataUnit, [key]: newValue }
      return updatedDataUnit
    })
  }

  const toNullableNumber = (value: number | string) => {
    if (value === '') return null

    const parsedValue = Number(value)
    return Number.isNaN(parsedValue) ? null : parsedValue
  }

  const onSave = async () => {
    const { recYear, smpYear, holidayYear, ...less } = dataUnit

    await updateDataUnit({
      variables: {
        val: {
          ...less,
          recYear: toNullableNumber(recYear),
          smpYear: toNullableNumber(smpYear),
          holidayYear: toNullableNumber(holidayYear),
        },
      },
    })
  }

  return (
    <>
      <ContentWrapper>
        <Header>
          <Title>단가 기본값</Title>
          <SaveButton onClick={() => setShowSavePopup(true)}>저장</SaveButton>
        </Header>
        <Body>
          <Left>
            <LabelInput
              label="전력시장 거래수수료 단가"
              placeholder="원/kWh"
              value={dataUnit?.powerMarketTransactionUnit.toString()}
              onChange={(e) =>
                handleDataUnitChange(
                  'powerMarketTransactionUnit',
                  Number(e.target.value)
                )
              }
              unit="원/kWh"
            />
            <LabelInput
              label="기후환경 요금 단가"
              placeholder="원/kWh"
              value={dataUnit?.environmentalUnit.toString()}
              onChange={(e) =>
                handleDataUnitChange(
                  'environmentalUnit',
                  Number(e.target.value)
                )
              }
              unit="원/kWh"
            />
            <LabelInput
              label="녹색 프리미엄 단가"
              placeholder="원/kWh"
              value={dataUnit?.greenPremiumUnit.toString()}
              onChange={(e) =>
                handleDataUnitChange('greenPremiumUnit', Number(e.target.value))
              }
              unit="원/kWh"
            />
            <InUnit>
              <LabelOnly label="기준 데이터" />
              <InUnitContent>
                <InUnitItem>
                  <InUnitLabel>한전 TOU 요금제</InUnitLabel>
                  <AutoCompleteOnly
                    options={kepcoOptions}
                    placeholder="한전 TOU 요금제"
                    value={dataUnit.touVer}
                    width={160}
                    setData={setDataUnit}
                    fieldName="touVer"
                  />
                </InUnitItem>
                <InUnitItem>
                  <InUnitLabel>현물 REC</InUnitLabel>
                  <AutoCompleteOnly
                    options={recOptions}
                    placeholder="현물 REC"
                    value={dataUnit.recYear}
                    setData={setDataUnit}
                    fieldName="recYear"
                  />
                </InUnitItem>
                <InUnitItem>
                  <InUnitLabel>SMP</InUnitLabel>
                  <AutoCompleteOnly
                    options={smpOptions}
                    placeholder="SMP"
                    value={dataUnit.smpYear}
                    setData={setDataUnit}
                    fieldName="smpYear"
                  />
                </InUnitItem>
                <InUnitItem>
                  <InUnitLabel>공휴일</InUnitLabel>
                  <AutoCompleteOnly
                    options={holidayOptions}
                    placeholder="공휴일"
                    value={dataUnit.holidayYear}
                    setData={setDataUnit}
                    fieldName="holidayYear"
                  />
                </InUnitItem>
              </InUnitContent>
            </InUnit>
            <InUnit>
              <LabelOnly label="내부망 단가" />
              <InUnitContent>
              <InUnitItem>
                <InUnitLabel>기본요금</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.internalNetworkUnitBaseUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'internalNetworkUnitBaseUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>전력량 요금</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.internalNetworkUnitPowerUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'internalNetworkUnitPowerUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
            </InUnitContent>
          </InUnit>
        </Left>
        <Right>
          <PPACost>
            <LabelOnly label="제 3자 PPA 비용" />
            <InUnitContent>
              <InUnitItem>
                <InUnitLabel>배전요금 (기본)</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.distributionUnitBaseUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'distributionUnitBaseUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>배전요금 (사용량)</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.distributionUnitUsageUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'distributionUnitUsageUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>손실비용 (저부하)</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.lossCostMinUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'lossCostMinUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>손실비용 (중간부하)</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.lossCostMidUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'lossCostMidUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>손실비용 (최대부하)</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.lossCostMaxUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'lossCostMaxUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>부가정산금</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.additionalSettlementUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'additionalSettlementUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>거래수수료</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.transactionCommissionUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'transactionCommissionUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>할인가격</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.discountedPriceUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'discountedPriceUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
              <InUnitItem>
                <InUnitLabel>송전손실비용</InUnitLabel>
                <InputOnly
                  placeholder="원/kWh"
                  value={dataUnit?.transmissionLossCostUnit.toString()}
                  onChange={(e) =>
                    handleDataUnitChange(
                      'transmissionLossCostUnit',
                      Number(e.target.value)
                    )
                  }
                  unit="원/kWh"
                />
              </InUnitItem>
            </InUnitContent>
          </PPACost>
          <LabelInput
            label="기타비용"
            placeholder="원/kWh"
            value={dataUnit?.ectCostUnit.toString()}
            onChange={(e) =>
              handleDataUnitChange('ectCostUnit', Number(e.target.value))
            }
            unit="원/kWh"
          />
        </Right>
        </Body>
      </ContentWrapper>
      {showSavePopup && (
        <ConfirmPopup
          type="save"
          title="저장하시겠습니까?"
          confirmText="저장하기"
          cancelText="돌아가기"
          onClose={() => setShowSavePopup(false)}
          onConfirm={onSave}
        />
      )}
    </>
  )
}
